import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  CheckCircle as CompletedIcon,
  PersonAdd as InviteIcon,
  AlternateEmail as MentionIcon,
  Circle as UnreadIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { useThemeMode } from '../contexts/ThemeContext';
import type { Notification, NotificationType } from '../types';

interface NotificationDropdownProps {
  onClose: () => void;
}

/**
 * 通知タイプに応じたアイコンを取得
 */
const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'mention':
      return <MentionIcon sx={{ color: '#667eea' }} />;
    case 'invite':
      return <InviteIcon sx={{ color: '#64ffda' }} />;
    case 'item_completed':
      return <CompletedIcon sx={{ color: '#4ade80' }} />;
    case 'assignment':
      return <AssignmentIcon sx={{ color: '#fbbf24' }} />;
    default:
      return <MentionIcon sx={{ color: '#667eea' }} />;
  }
};

/**
 * 相対時間をフォーマット
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'たった今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;

  return date.toLocaleDateString('ja-JP', {
    month: 'short',
    day: 'numeric',
  });
};

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { themeMode } = useThemeMode();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  } = useNotifications();

  // Theme colors
  const containerBg = themeMode === 'dark' ? 'rgba(30, 58, 95, 0.95)' : 'rgba(255, 255, 255, 0.98)';
  const containerBorder =
    themeMode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)';
  const containerShadow =
    themeMode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)';
  const headerBorder = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
  const titleColor = themeMode === 'dark' ? '#e2e8f0' : '#1a1a2e';
  const accentColor = themeMode === 'dark' ? '#64ffda' : '#00897b';
  const accentBorder = themeMode === 'dark' ? 'rgba(100, 255, 218, 0.4)' : 'rgba(0, 137, 123, 0.4)';
  const accentHoverBg =
    themeMode === 'dark' ? 'rgba(100, 255, 218, 0.15)' : 'rgba(0, 137, 123, 0.1)';
  const emptyTextColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)';
  const unreadBg = themeMode === 'dark' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.08)';
  const unreadHoverBg =
    themeMode === 'dark' ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.12)';
  const readHoverBg = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)';
  const notificationTitle = themeMode === 'dark' ? '#e2e8f0' : '#1e293b';
  const notificationBody = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
  const notificationTime = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
  const deleteIconColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
  const dividerColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)';

  const handleNotificationClick = async (notification: Notification) => {
    // 未読の場合は既読にする
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // リンクがある場合は遷移
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation(); // 親要素のクリックイベントを止める
    await deleteNotification(notificationId);
  };

  const handleClearAll = async () => {
    await deleteAllNotifications();
  };

  return (
    <Box
      sx={{
        width: 360,
        maxHeight: 480,
        backgroundColor: containerBg,
        border: `1px solid ${containerBorder}`,
        borderRadius: 2,
        boxShadow: containerShadow,
        overflow: 'hidden',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pl: 2.5,
          pr: 1.5,
          py: 0.75,
          borderBottom: `1px solid ${headerBorder}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: titleColor }}>
            通知
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: '#ef4444',
                color: '#fff',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              sx={{
                fontSize: '0.7rem',
                color: accentColor,
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                border: `1px solid ${accentBorder}`,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: accentHoverBg,
                  borderColor: accentColor,
                },
              }}
            >
              すべて既読
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              size="small"
              onClick={handleClearAll}
              sx={{
                fontSize: '0.7rem',
                color: '#ef4444',
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  borderColor: '#ef4444',
                },
              }}
            >
              すべてクリア
            </Button>
          )}
        </Box>
      </Box>

      {/* 通知一覧 */}
      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: accentColor }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
            <Typography variant="body2" sx={{ color: emptyTextColor }}>
              通知はありません
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItemButton
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    backgroundColor: notification.read ? 'transparent' : unreadBg,
                    '&:hover': {
                      backgroundColor: notification.read ? readHoverBg : unreadHoverBg,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            fontWeight: notification.read ? 400 : 600,
                            color: notificationTitle,
                            flex: 1,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <UnreadIcon
                            sx={{
                              fontSize: 8,
                              color: '#667eea',
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: notificationBody,
                            display: 'block',
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.body}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: notificationTime,
                            fontSize: '0.65rem',
                          }}
                        >
                          {formatRelativeTime(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteNotification(e, notification.id)}
                    sx={{
                      ml: 0.5,
                      color: deleteIconColor,
                      '&:hover': {
                        color: '#ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ListItemButton>
                {index < notifications.length - 1 && <Divider sx={{ borderColor: dividerColor }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default NotificationDropdown;
